import { cx } from "@emotion/css";
import { DesktopDatePicker, LocalizationProvider, PickersDay } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isAfter, isSameDay, isWithinInterval } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import useDeviceType from "hooks/useDeviceType";
import useOpenClose from "hooks/useOpenClose";
import SecondaryButton from "library/Atoms/Button/SecondaryButton";
import InputLayout from "library/Layouts/InputLayout";
import { PropTypes } from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate, formatDateNumeric, noonOfDay } from "utility/date-fns";
import { getDateFnsLocale } from "utility/helpers/locales";
import { DEFAULT_TIMEZONE } from "../../../../constants";
import { DatePickerInput } from "../DatePicker";
import styles from "./styles";

const DateRangePickerDay = ({ day, startDate, endDate, hoveredDate, timezone, isFuture, onClick, ...restProps }) => {
  const isStartDate = useMemo(() => startDate && isSameDay(day, startDate), [day, startDate]);
  const isEndDate = useMemo(() => endDate && isSameDay(day, endDate), [day, endDate]);
  const isBetween = useMemo(() => {
    if (!startDate || !endDate) return false;
    if (isAfter(startDate, endDate)) return false;

    return isWithinInterval(day, { start: startDate, end: endDate });
  }, [startDate, endDate, day]);
  const isToday = useMemo(() => isSameDay(day, utcToZonedTime(new Date(), timezone)), [day, timezone]);

  const isHovered = useMemo(() => {
    if (!hoveredDate || !startDate || endDate) return false;
    if (isAfter(startDate, hoveredDate)) return false;

    return isWithinInterval(day, { start: startDate, end: hoveredDate });
  }, [hoveredDate, startDate, endDate, day]);

  const isHoveredStart = useMemo(() => {
    if (!hoveredDate || !startDate || endDate) return false;
    if (isAfter(startDate, hoveredDate)) return false;
    return isSameDay(day, startDate);
  }, [hoveredDate, startDate, endDate, day]);

  const isHoveredEnd = useMemo(() => {
    if (!hoveredDate || !startDate || endDate) return false;
    if (isAfter(startDate, hoveredDate)) return false;
    return isSameDay(day, hoveredDate);
  }, [hoveredDate, startDate, endDate, day]);

  const pickerDayStyles = useMemo(() => {
    if (isFuture) return [styles.disabledDay];

    if (isHovered) {
      const isFirstHoveredDay = isSameDay(day, startDate);
      const isLastHoveredDay = isSameDay(day, hoveredDate);

      if (isFirstHoveredDay && isLastHoveredDay) {
        // First and last day of hovered range: rounded borders
        return [styles.hoveredDay, styles.roundedLeftBorder, styles.roundedRightBorder];
      }
      if (isLastHoveredDay) {
        // Last day of hovered range: rounded right border
        return [styles.hoveredDay, styles.squareLeftBorder, styles.roundedRightBorder];
      }
      if (isFirstHoveredDay) {
        // First day of hovered range: rounded left border
        return [styles.hoveredDay, styles.roundedLeftBorder, styles.squareRightBorder];
      }
      // Middle days of hovered range: square borders
      return [styles.hoveredDay, styles.squareLeftBorder, styles.squareRightBorder];
    }
    if (isHoveredStart) {
      return [
        styles.hoveredDay,
        styles.roundedLeftBorder,
        hoveredDate && !isSameDay(startDate, hoveredDate) ? styles.squareRightBorder : styles.roundedRightBorder,
      ];
    }
    if (isHoveredEnd) {
      return [
        styles.hoveredDay,
        styles.roundedRightBorder,
        startDate && !isSameDay(startDate, hoveredDate) && styles.squareLeftBorder,
      ];
    }

    if (isStartDate) {
      // Start date: rounded left border, square right border (when part of range)
      // If no end date selected yet, show as single day with rounded borders
      if (!endDate) {
        return [styles.edgeDay, styles.roundedLeftBorder, styles.roundedRightBorder];
      }
      return [
        styles.edgeDay,
        styles.roundedLeftBorder,
        endDate && !isSameDay(startDate, endDate) && styles.squareRightBorder,
      ];
    }
    if (isEndDate) {
      // End date: rounded right border, square left border (when part of range)
      return [
        styles.edgeDay,
        styles.roundedRightBorder,
        startDate && !isSameDay(startDate, endDate) && styles.squareLeftBorder,
      ];
    }
    if (isBetween) {
      // Between dates: square borders on both sides
      return [styles.betweenDay, styles.squareLeftBorder, styles.squareRightBorder];
    }

    return null;
  }, [
    day,
    endDate,
    hoveredDate,
    isBetween,
    isEndDate,
    isHovered,
    isHoveredEnd,
    isHoveredStart,
    isStartDate,
    isFuture,
    startDate,
  ]);

  const { disabled: pickersDisabled, ...otherProps } = restProps;

  return (
    <PickersDay
      day={day}
      className={cx(styles.day, pickerDayStyles, isToday && styles.today)}
      selected={false}
      disableHighlightToday
      aria-label={formatDate(noonOfDay(day), timezone)}
      {...otherProps}
      disabled={pickersDisabled || isFuture}
      onClick={isFuture ? () => {} : onClick}
    />
  );
};

DateRangePickerDay.defaultProps = {
  startDate: null,
  endDate: null,
  hoveredDate: null,
};

DateRangePickerDay.propTypes = {
  day: PropTypes.instanceOf(Date).isRequired,
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  hoveredDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  timezone: PropTypes.string.isRequired,
  isFuture: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const DateRangePicker = ({
  id,
  name,
  value,
  label,
  placeholder,
  onSelect,
  renderInput,
  timezone,
  required,
  disabled,
  ...restProps
}) => {
  const { t } = useTranslation();
  const { isMobile } = useDeviceType();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [isPickerOpen, openPicker, closePicker] = useOpenClose(false);
  const [inputValue, setInputValue] = useState(null);

  const [originalStartDate, setOriginalStartDate] = useState(null);
  const [originalEndDate, setOriginalEndDate] = useState(null);
  const [originalInputValue, setOriginalInputValue] = useState(null);

  const today = useMemo(() => {
    const tz = timezone || DEFAULT_TIMEZONE;
    const zonedNow = utcToZonedTime(new Date(), tz);
    return new Date(zonedNow.getFullYear(), zonedNow.getMonth(), zonedNow.getDate());
  }, [timezone]);

  const clearDates = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setHoveredDate(null);
    onSelect(name, [null, null]);
    setInputValue(null);
    closePicker();
  }, [onSelect, name, closePicker]);

  const handleOpenPicker = useCallback(() => {
    setOriginalStartDate(startDate);
    setOriginalEndDate(endDate);
    setOriginalInputValue(inputValue);
    openPicker();
  }, [startDate, endDate, inputValue, openPicker]);

  const handleDateSelection = useCallback(
    (date) => {
      // Ensure the date is properly normalized to avoid timezone issues
      const normalizedDate = noonOfDay(new Date(date.getFullYear(), date.getMonth(), date.getDate()));

      if (!startDate) {
        // First date selection
        setStartDate(normalizedDate);
        setHoveredDate(normalizedDate);
      } else if (!endDate) {
        // Second date selection
        if (isSameDay(normalizedDate, startDate)) {
          // Same day selected - apply single day range immediately
          setEndDate(normalizedDate);
          const selectedRange = [normalizedDate, normalizedDate];
          onSelect(name, selectedRange);
          closePicker();
          const newInputValue = formatDateNumeric(normalizedDate);
          setInputValue(newInputValue);
        } else if (isAfter(startDate, normalizedDate)) {
          // Reverse selection - ignore and keep only start date
        } else {
          // Forward selection - apply range immediately
          setEndDate(normalizedDate);
          const selectedRange = [startDate, normalizedDate];
          onSelect(name, selectedRange);
          closePicker();
          const newInputValue = `${formatDateNumeric(startDate)} - ${formatDateNumeric(normalizedDate)}`;
          setInputValue(newInputValue);
        }
      } else {
        // Reset and start new selection
        setStartDate(normalizedDate);
        setEndDate(null);
        setHoveredDate(normalizedDate);
      }
    },
    [startDate, endDate, onSelect, name, closePicker, timezone]
  );

  const handleHover = useCallback(
    (date) => {
      if (startDate && !endDate) {
        const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        setHoveredDate(normalizedDate);
      }
    },
    [startDate, endDate]
  );
  const clearHover = useCallback(() => setHoveredDate(null), []);

  const handleClose = useCallback(() => {
    const isClearedInParent = !value?.[0] && !value?.[1];
    // Check if changes were applied (endDate is set and different from original)
    const hasChanges = endDate !== originalEndDate || startDate !== originalStartDate;
    const hasUnappliedChanges = hasChanges && !endDate && !isClearedInParent;

    if (hasUnappliedChanges) {
      // Revert to original state if changes weren't applied
      setStartDate(originalStartDate);
      setEndDate(originalEndDate);
      setInputValue(originalInputValue);
      setHoveredDate(null);
    }

    closePicker();
    clearHover();
  }, [clearHover, closePicker, endDate, originalEndDate, originalInputValue, originalStartDate, startDate, value]);

  useEffect(() => {
    const [newStart, newEnd] = value || [];

    const parseDate = (date) => {
      if (!date) return null;
      return date instanceof Date ? date : new Date(date);
    };

    const parsedStart = parseDate(newStart);
    const parsedEnd = parseDate(newEnd);

    setStartDate(parsedStart);
    setEndDate(parsedEnd);

    if (parsedStart && parsedEnd) {
      setInputValue(`${formatDateNumeric(parsedStart, timezone)} - ${formatDateNumeric(parsedEnd, timezone)}`);
    } else if (!parsedStart && !parsedEnd) {
      setInputValue(null);
    }
  }, [value, timezone]);

  return (
    <LocalizationProvider adapterLocale={getDateFnsLocale()} dateAdapter={AdapterDateFns}>
      <InputLayout name={name} label={label} required={required}>
        <DesktopDatePicker
          open={isPickerOpen}
          value={startDate || new Date()}
          onChange={() => {}}
          onClose={handleClose}
          PopperProps={{
            placement: (() => {
              if (isMobile) return "auto";
              return renderInput ? "top-start" : "bottom";
            })(),
            "data-cy": `${id}-menu`,
          }}
          PaperProps={{ onMouseLeave: clearHover }}
          renderDay={(date, _, params) => {
            const isFuture = isAfter(date, today);

            return (
              <div key={date} onMouseEnter={() => !isFuture && handleHover(date)} onMouseLeave={clearHover}>
                <DateRangePickerDay
                  day={date}
                  startDate={startDate}
                  endDate={endDate}
                  hoveredDate={hoveredDate}
                  timezone={timezone}
                  isFuture={isFuture}
                  onClick={() => handleDateSelection(date)}
                  {...params}
                />
              </div>
            );
          }}
          renderInput={({ inputProps, ...restParams }) =>
            renderInput ? (
              renderInput({ open: handleOpenPicker, value: inputValue, ...restParams })
            ) : (
              <DatePickerInput
                id={id}
                name={name}
                value={inputValue}
                placeholder={placeholder}
                onClick={handleOpenPicker}
                onClear={clearDates}
                clearable={!!inputValue}
                {...restParams}
              />
            )
          }
          ToolbarComponent={() => (
            <div className="d-flex flex-column align-items-center gap-3 mx-4 mb-4 order-1">
              <SecondaryButton
                id="clear-date-range"
                className={cx(styles.toolbarBtn, "col-12 px-0 order-1")}
                label={t("utils.ui.clearBtn")}
                onClick={clearDates}
                disabled={!startDate && !endDate}
              />
            </div>
          )}
          maxDate={today}
          disabled={disabled}
          views={["day"]}
          closeOnSelect={false}
          leftArrowButtonText=""
          rightArrowButtonText=""
          showToolbar
          {...restProps}
        />
      </InputLayout>
    </LocalizationProvider>
  );
};

DateRangePicker.defaultProps = {
  name: "",
  label: null,
  value: [null, null],
  placeholder: null,
  renderInput: null,
  timezone: null,
  required: false,
  disabled: false,
};

DateRangePicker.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  renderInput: PropTypes.func,
  timezone: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default DateRangePicker;
